import { clamp } from 'lodash';
import { VesselArea, VesselSpeedType } from '../../graphql';
import type { OrderId } from '../../graphql';
import { dToMs, hToMs, msToH } from '../../lib/time';
import {
  assertedCast,
  exhaustiveCheck,
  findIndexWith,
  get,
  groupBySequence,
  isDefined,
  mapKeyBy,
  memoize,
  setFind,
  withDefined,
} from '../../lib/util';
import { DEFAULT_CARBON_FACTOR } from '../pnl/co2/euEts';
import type { LocationId, OmitFunctions, ScheduleData } from './Core';
import {
  calculateSeaDuration,
  calculateWorkShifts,
  ceilIfEnabled,
  DELAY_CONSTRAINT_HOURS,
  determinePortArea,
  determineVesselArea,
  fallbackBallastSpeed,
  fallbackLadenSpeed,
  getDefaultVesselSpeedByType,
  getEventKey,
  getEventLocation,
  getEventOrder,
  getOrderLaytimeDays,
  isPortCallEventStartSourced,
} from './Core';
import {
  getEffectiveCargoHandlingRate,
  getLoadingTime,
} from './Core.loading-discharging';
import type { ClientId } from './ClientId';
import type { Vessel } from './Vessel';
import { getVesselBannedChannels, getVesselCapacities } from './Vessel';
import { getAssortmentExclusionsByShipmentId } from './assortmentExclusions';
import { minimumEventDuration } from '@seaber/common/core/entries';
import { getNearestSpeedConsumption } from './Core.bunker-consumption';
import {
  chooseLoadedPieces,
  isPerPieceCargoUnit,
} from '../../constants/cargoUnits';
import type { LoadedCompartment } from './Core.cargo-allocation';
import {
  getAndAddLoadedAmountToCompartments,
  getCompartments,
  getMaxAllowedLoadAmount,
  getMaxAllowedLoadAmountCacheKey,
  getOverlappingLoadsByOrder,
  getVoyageExclusionsById,
  isCorrectCargoAllocationType,
  releaseOrderFromCompartments,
} from './Core.cargo-allocation';
import type {
  CalculationConfig,
  ScheduleDataContext,
} from './ScheduleDataContext';
import type {
  BoundaryVisitResolver,
  CalculatedWork,
  CargoEvent,
  EnrichedSpeedConsumption,
  Events,
  TimeData,
} from './TimeData';
import {
  canEventBeConcurrent,
  CoreEventType,
  createBoundaryVisitResolver,
  getBoundaryPairPreviousPortCallId,
  getEventEndLocationId,
  getEventLocationId,
  getEventStartLocationId,
  isCargoEvent,
  isCargoEventTimeData,
  isDeckCargo,
  isDischargeEvent,
  isEventConcurrent,
  isLoadEvent,
  isOffHireEventTimeData,
  isStaticEventTimeData,
  isPortCallArrivalEvent,
  isPortCallBerthingEvent,
  isPortCallDepartureEventTimeData,
  isPortCallEvent,
  isPortCallEventTimeData,
  isSingleEvent,
  SingleEventType,
  isTcInEvent,
} from './TimeData';
import type { DeriveCapacitiesAndTimesState } from './TimeDataCache';
import {
  cacheTimeData,
  cloneDeriveCapacitiesAndTimesState,
  getCachedTimeData,
} from './TimeDataCache';
import { stringToNumOpt } from './util';
import {
  buildDraftMassCeiling,
  getDraftLoadReservationDigest,
} from './onBoardDraftSegments';
import type { PortCostEstimateMaps } from '../hooks/usePortCostEstimates';
import { getDefaultPortCostByType } from '../hooks/usePortCostEstimates';
import { getEventShipmentId } from '../shipment/shipment';
import { getHistoricalPortCostEstimates } from '../shipment/costs/portCostHistory';
import { getMaxDefaultPortCostForLocation } from '../shipment/costs/portCostDefault';
import type { PortCostPriority } from '../shipment/costs/costs';
import {
  DEFAULT_PORT_COST_PRIORITY,
  portCostPriorityKeys,
} from '../shipment/costs/costs';
import { type ChannelCostMatchCriteria } from '../pnl/channelCost/channelCostIndex';
import { getEffectiveChannelCost } from '../pnl/channelCost/channelCostUtils';
import {
  ChannelCostCargoCondition,
  PortType,
} from '@seaber/common/graphql/types';
import { mapGetFirstEntry } from '@seaber/common/lib/collections';
import { getLatestHistoricalPortCost } from '../shipment/costs/portCostLatestHistory';
import { getBannedChannelsInRoute } from '../distances/distance-utils';

/** Constant params required for timeline derivation, collected into an object for easier parameter drilling */
export type DeriveCapacitiesAndTimesParams = {
  vessel: Vessel;
  context: Pick<
    ScheduleDataContext,
    'coasById' | 'distances' | 'portCostEstimateMaps'
  >;
  config: OmitFunctions<CalculationConfig>;
  now?: number;
};

export type DeriveCapacitiesAndTypesOptions = {
  skipSwapComputation?: boolean;
  calculateCosts: boolean;
  useDbValues?: boolean;
  writeTimeDataCache?: boolean;
  /**
   * The location the vessel was at before the first event.
   * If provided, DCAT will calculate sea distance from this location to the first event.
   * If not provided, DCAT assumes the vessel starts at the first event's location.
   */
  initialLocationId?: LocationId;
  /** Per-order target fill ratio (orderId → ratio of the order's MAX quantity), supplied by the
   * income-prioritized fill overlay for suggestions and committed voyages alike. When present, the
   * load branch uses the order's ratio in place of the getMaxAllowedLoadAmount/min(...,1) fill: a ratio
   * at or above the band-minimum floor is clamped to [floorRatio, capacityFeasibleMax] (a raise), while
   * a ratio below the floor is honored as an income-ordered reduction — capped above by
   * capacityFeasibleMax, but never inflated back up to the floor. Absent for all non-overlay callers,
   * so default DCAT behavior is unchanged. Never persisted; bypasses the TimeDataCache and lives only
   * for one re-derive call. */
  explicitLoadRatios?: ReadonlyMap<OrderId, number>;
};

/**
 * Calculate movable mass, amount of time required, buffers and violations and costs
 */
export const DeriveCapacitiesAndTimes = <EventType extends Events>(
  vessel: DeriveCapacitiesAndTimesParams['vessel'],
  events: EventType[],
  // TODO Where should we take initial location and start time / first event to consider ?
  // TODO Last event that has already happened / started ?
  {
    coasById,
    distances,
    portCostEstimateMaps,
    voyageExclusions,
  }: Pick<
    ScheduleDataContext,
    | 'coasById'
    | 'distances'
    | 'portCostEstimateMaps'
    | 'voyageExclusions'
    | 'locations'
    | 'currentUser'
  >,
  config: OmitFunctions<CalculationConfig>,
  {
    skipSwapComputation = false,
    useDbValues = false,
    calculateCosts,
    writeTimeDataCache = false,
    initialLocationId,
    explicitLoadRatios,
  }: DeriveCapacitiesAndTypesOptions,
  now: number = Date.now(),
): ScheduleData<EventType> => {
  const {
    massCapacity: weightCapacity,
    volumeCapacity,
    deckVolumeCapacity,
  } = getVesselCapacities(vessel);
  // A dimension the vessel does not define is unknown, and unknown headroom caps nothing.
  const massCap = weightCapacity ?? Infinity;
  const volumeCap = volumeCapacity ?? Infinity;
  const deckVolumeCap = deckVolumeCapacity ?? Infinity;
  const tcRate = Number(vessel.tcRate ?? 0) / 24;

  const bannedChannels = getVesselBannedChannels(vessel);

  const firstEvent: Events | undefined = events[0];

  const speedLaden = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.LADEN,
    fallbackLadenSpeed,
  );
  const speedBallast = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.BALLAST,
    fallbackBallastSpeed,
  );

  const firstRealEventIndex = findIndexWith(events, 0, 1, (e) => !e.skipOnSwap);
  const lastRealEventIndex = findIndexWith(
    events,
    events.length - 1,
    -1,
    (e) => !e.skipOnSwap,
  );
  if (
    (firstRealEventIndex === undefined || lastRealEventIndex === undefined) &&
    events.length > 0
  ) {
    console.warn(`Event batch doesn't have any real, non-skippable events`);
  }

  const overlappingLoadsByOrder = getOverlappingLoadsByOrder(events);
  // Built once per derivation; the common zero-pair timeline pays one scan
  const boundaryVisitResolver = createBoundaryVisitResolver(events);
  // Per-segment port-draft mass ceiling for this derive, or null when the schedule imposes no draft
  // cap. A null ceiling leaves the load branch and cache keys byte-identical to a draftless derive,
  // so the common unconstrained case pays only one cheap scan for a located port draft.
  const draftCeiling = buildDraftMassCeiling(events, vessel);
  // The cache key for each load's amount, for the inputs the (event, prevTd) key misses: the later
  // co-loaded set getMaxAllowedLoadAmount reserves against, and the draft profile of the spans the
  // load transits. Both digests are derived by their co-located key functions so they stay in lockstep
  // (getMaxAllowedLoadAmountCacheKey, getDraftLoadReservationDigest).
  const loadAmountCacheKeyByEvent = new Map<Events, string | null>();
  for (const event of events) {
    if (isLoadEvent(event)) {
      const reservationKey = getMaxAllowedLoadAmountCacheKey(
        overlappingLoadsByOrder.get(event.order.id) ?? [],
      );
      const draftDigest = draftCeiling
        ? getDraftLoadReservationDigest(
            draftCeiling.segments,
            draftCeiling.segmentIndicesByOrderId.get(event.order.id) ?? [],
          )
        : null;
      loadAmountCacheKeyByEvent.set(
        event,
        draftDigest === null
          ? reservationKey
          : `${reservationKey}|${draftDigest}`,
      );
    }
  }

  // Get initial items from cache. An explicitLoadRatios overlay changes the loaded amount but is not
  // part of the cache key, so an overlay re-derive bypasses the cache and does not write it.
  const {
    startIndex,
    timeData: arr,
    state: cachedState,
  } = explicitLoadRatios && explicitLoadRatios.size > 0
    ? {
        startIndex: 0,
        state: null,
        timeData: new Array<TimeData<EventType>>(events.length),
      }
    : getCachedTimeData<EventType>(events, loadAmountCacheKeyByEvent);

  // Collect any mutated values to state so we may cache and restore it
  const state: DeriveCapacitiesAndTimesState = cachedState ?? {
    // Use initialLocationId if provided, otherwise assume vessel starts at first event's location
    prev:
      initialLocationId ??
      withDefined(firstEvent, getEventStartLocationId) ??
      (-1 as LocationId),
    portCallArrivalIndex: null,
    /** Start time of the first event in the current port call. The same value for every event in the same port call. */
    arriveInPort: firstEvent?.startOverride ?? firstEvent?.start ?? now,
    previousEnd: firstEvent?.startOverride ?? firstEvent?.start ?? now,
    volumeCapacityAvailable: volumeCap,
    deckVolumeCapacityAvailable: deckVolumeCap,
    massCapacityAvailable: massCap,
    massAboard: 0,
    holdVolumeAboard: 0,
    deckVolumeAboard: 0,
    minMassUsed: 0,
    minHoldVolumeUsed: 0,
    minDeckVolumeUsed: 0,
    loadedCompartmentsById: mapKeyBy(
      getCompartments(vessel).map(
        (c): LoadedCompartment => ({
          compartment: c,
          allocations: [],
          assortment: null,
        }),
      ),
      (c) => c.compartment.id,
    ),
    totalHoursAtSea: 0,
    totalHoursAtPort: 0,
    ballastDistance: 0,
    totalDistance: 0,
    unresolvedLegs: 0,
    bunkerCost: 0,
    portCostEstimateTotal: 0,
    totalMass: 0,
    delays: 0,
    constrainedDelays: 0,
    tcCostTotal: 0,
    cost: 0,
    orderToLoadIndex: new Map<number, number>(),
    hasFirstShipmentEvent: false,
    concurrentPortHours: 0,
    currentPortCallOperationsStart: null,
    currentPortCallLastDischargeEnd: null,
    ordersOnBoard: 0,
    carriedAssortmentsByOrderId: new Map(),
  };

  const {
    assortmentExclusions,
    bunkerPriceByGradeAndPortCode,
    orgDefaultBunkerGradeIdsByArea,
    bunkerGradeCalculations,
    resolvedBunkerGrades,
  } = config;

  const assortmentExclusionsByShipmentId = getAssortmentExclusionsByShipmentId(
    vessel,
    events,
    assortmentExclusions,
  );

  const voyageExclusionsById = getVoyageExclusionsById(voyageExclusions);

  // The per-span decided sums are per-derive locals, not part of the cached state. On a cache resume,
  // replay the reused prefix loads into them so each span's budget continues from where the cached
  // prefix left off; the load's own draft profile is folded into its reservation key, so a resumed
  // prefix load already matches the ceiling it was derived against.
  if (draftCeiling && startIndex > 0) {
    for (let i = 0; i < startIndex; i++) {
      const cachedTd = arr[i];
      if (cachedTd && isLoadEvent(cachedTd.event)) {
        const segmentIndices = draftCeiling.segmentIndicesByOrderId.get(
          cachedTd.event.order.id,
        );
        if (segmentIndices) {
          for (const s of segmentIndices) {
            draftCeiling.decidedMass[s] += cachedTd.mass;
            draftCeiling.decidedMinMass[s] +=
              cachedTd.event.order.cargoRanges.mass.min;
          }
        }
      }
    }
  }

  for (let i = startIndex, l = events.length; i < l; ++i) {
    const event = events[i];
    const prevTd = get(arr, i - 1);
    const nextEvent = get(events, i + 1);

    const isLastEvent = i === lastRealEventIndex;
    const isFirstEvent = i === firstRealEventIndex;

    const laden = state.ordersOnBoard > 0;

    const {
      constraints,
      startOverride,
      durationOverride,
      seaDurationOverride: savedSeaDurationOverride,
      sofCommenced,
      sofDuration,
      category,
      type,
    } = event;

    const inputs: TimeData['inputs'] = {};

    const startLocationId = getEventStartLocationId(event);
    const endLocationId = getEventEndLocationId(event);
    const isPortCallArrival = isPortCallArrivalEvent(event);
    let avoidsChannels =
      (isPortCallArrival && event.legAvoidsChannels) ?? false;

    if (prevTd && isPortCallDepartureEventTimeData(prevTd)) {
      state.portCallArrivalIndex = null;
    }
    if (isPortCallArrival) {
      state.portCallArrivalIndex = i;
    }

    /** The PortCallArrivalEvent of the current port call */
    const currentPortCallArrival =
      state.portCallArrivalIndex === null
        ? null
        : assertedCast(
            events[state.portCallArrivalIndex],
            isPortCallArrivalEvent,
          );

    /** Did the location change between the previous and current event **/
    const changeLocation = state.prev !== -1 && startLocationId !== state.prev;

    const routeWithChannels = changeLocation
      ? distances.get(state.prev, startLocationId, false)
      : undefined;

    const bannedChannelsInRoute = getBannedChannelsInRoute(
      routeWithChannels,
      bannedChannels,
    );

    if (bannedChannelsInRoute.length > 0) {
      avoidsChannels = true;
    }

    // An unusable direct entry carries no channels to read, so without this a
    // restricted vessel calls the leg unresolved while the avoiding variant it
    // may sail sits in the matrix. getLegRoute makes the same read for the
    // rotation paths, and the two must agree or a drag and a recalculation rank
    // the same leg differently.
    if (
      !avoidsChannels &&
      bannedChannels.size > 0 &&
      (!routeWithChannels || routeWithChannels.unresolved === true)
    ) {
      avoidsChannels = true;
    }

    const routeData = changeLocation
      ? distances.get(state.prev, startLocationId, avoidsChannels)
      : undefined;
    const distance = routeData?.dist || 0;
    // A leg with no route reads 0 here, which is also a legitimate distance
    // between two locations at the same position. Keep the two apart.
    const distanceUnresolved =
      changeLocation && (!routeData || routeData.unresolved === true);
    state.totalDistance += distance;
    if (distanceUnresolved) {
      state.unresolvedLegs += 1;
    }

    if (!laden) {
      state.ballastDistance += distance;
    }

    let portCostEstimate = 0;
    let channelCost = 0;
    /** Hours */
    let channelDurationOverhead = 0;

    let extraPortHours = 0;
    let extraPortDuration = 0;
    const isPortArrivalForSuggestions =
      !isPortCallArrival && (changeLocation || i == 0);
    const isCanalTransitPortCall =
      isPortCallEvent(event) && event.isCanalTransitPortCall;
    /** Arrival/berthing on the load side of a consecutive-COA boundary pair:
     * the vessel is already alongside from the discharge side's visit, so
     * these derive zero duration and loading starts at that departure. */
    const isBoundaryPairContinuation =
      (isPortCallArrivalEvent(event) || isPortCallBerthingEvent(event)) &&
      getBoundaryPairPreviousPortCallId(event) !== null;
    if (isPortArrivalForSuggestions) {
      const location = getEventLocation(event, config.locationsById);
      extraPortHours = (stringToNumOpt(location?.extraPortDays) || 0) * 24;
      extraPortDuration = hToMs(extraPortHours);
    }
    if (
      calculateCosts &&
      vessel.name &&
      (changeLocation || i == 0) &&
      portCostEstimateMaps &&
      type !== CoreEventType.OffHire
    ) {
      if (!isCanalTransitPortCall) {
        const portCallId = isPortCallEvent(event) ? event.portCallId : null;
        const portType = getPortTypeFromEvents(
          events,
          portCallId,
          boundaryVisitResolver,
        );
        const portCost = getEffectivePortCost(
          event,
          portCostEstimateMaps,
          config.portCostPriority,
          vessel as typeof vessel & { name: string }, // Name asserted above
          config.locationsById,
          portType,
        );
        state.portCostEstimateTotal += portCost;
        portCostEstimate = portCost;
      }

      if (routeData?.used_channels && routeData.used_channels.length > 0) {
        const channelCostResult = getChannelCost(
          event,
          routeData.used_channels,
          laden,
          state.carriedAssortmentsByOrderId,
          vessel,
          portCostEstimateMaps,
          config.vesselAttributeById,
          now,
        );
        channelCost = channelCostResult.channelCost ?? 0;
        channelDurationOverhead = channelCostResult.channelDurationOverhead;
      }
    }

    const isFirstShipmentEvent: boolean =
      !state.hasFirstShipmentEvent &&
      type !== CoreEventType.OffHire &&
      type !== CoreEventType.Static;

    const isConcurrentWithPrevious =
      startLocationId === state.prev &&
      isEventConcurrent(event) &&
      prevTd &&
      canEventBeConcurrent(prevTd.event);

    const eventStartIsSourced =
      isPortCallEvent(event) && isPortCallEventStartSourced(event);

    /** Ensure that origin is the same as it was when recording sea duration value */
    const seaDurationIsValid =
      !isPortCallArrivalEvent(event) ||
      !state.prev ||
      !event.previousLocationId ||
      event.previousLocationId === state.prev;

    const seaDurationOverride =
      savedSeaDurationOverride === null || !seaDurationIsValid
        ? null
        : eventStartIsSourced && startOverride
          ? clamp(
              savedSeaDurationOverride,
              minimumEventDuration,
              startOverride - state.previousEnd,
            )
          : savedSeaDurationOverride;

    /** Trivial when sea duration override is set. When no explicit seaDurationOverride, if there is a start override then we override the sea duration with the time difference. */
    const effectiveSeaDurationOverride =
      seaDurationOverride ??
      (isPortCallEvent(event) && startOverride
        ? Math.max(1, startOverride - state.previousEnd)
        : null);

    /** Sea duration from previous event. Always 0 between events in the same port call. */
    const calculatedSeaDuration = useDbValues
      ? Math.max(0, event.start - state.previousEnd)
      : calculateSeaDuration(
          speedLaden,
          speedBallast,
          distance,
          laden,
          config.useRoundedEstimations,
        ) + hToMs(channelDurationOverhead);

    const isEtaOverride =
      changeLocation && effectiveSeaDurationOverride !== null;
    /** Sea duration if this event is the first one in this port. 0 for all other events. */
    let hoursAtSea = isEtaOverride
      ? msToH(effectiveSeaDurationOverride)
      : msToH(calculatedSeaDuration);

    if (changeLocation) {
      state.arriveInPort = useDbValues
        ? event.start
        : state.previousEnd +
          (effectiveSeaDurationOverride ?? hToMs(hoursAtSea));
      state.currentPortCallOperationsStart = null;
      state.currentPortCallLastDischargeEnd = null;
    }

    /** Current running time, earliest possible placement for current event */
    const arriveToEvent =
      isConcurrentWithPrevious && prevTd
        ? prevTd.start
        : useDbValues
          ? event.start
          : state.previousEnd +
            ((changeLocation && effectiveSeaDurationOverride) ||
              hToMs(hoursAtSea)) +
            (isPortArrivalForSuggestions ? extraPortDuration : 0);

    const { layday, rta } = constraints;

    /** Eta-basis violation arrival; a continuation load uses the prior discharge end. */
    const violationArrival =
      isLoadEvent(event) && state.currentPortCallLastDischargeEnd !== null
        ? state.currentPortCallLastDischargeEnd
        : state.arriveInPort;

    /** The time this event can actually start considering override and running time */
    const startPortActivity =
      type === CoreEventType.OffHire
        ? event.start
        : startOverride
          ? isFirstShipmentEvent
            ? startOverride
            : Math.max(arriveToEvent, startOverride)
          : Math.max(arriveToEvent, event.delayUntil ?? 0);

    /** If this event's start override is sooner than possible (before previous event ended) */
    const hoursDelayConstraint = startOverride
      ? msToH(startPortActivity - arriveToEvent)
      : 0;

    /** Hours */
    let cancellingOrDeliveryViolationVsEta;
    /** Hours */
    let cancellingOrDeliveryViolationVsOperations;
    /** Hours */
    let cancellingOrDeliveryViolationVsEvent;
    let hasMiddle = false;
    let calculatedDuration: number;
    let work: CalculatedWork | undefined = undefined;
    let duration: number;
    let amount = 1;

    let start: number;

    const nextEventStartOverride = nextEvent?.startOverride ?? null;

    /** Actual mass loaded considering vessel capacity */
    let massLoaded = 0;
    /** Actual volume loaded considering vessel capacity */
    let volumeLoaded = 0;
    /** Cargo volume stowed on hold */
    let holdVolume = 0;
    /** Cargo volume stowed on deck */
    let deckVolume = 0;

    let pcs = 0;
    let cleaningCost = 0;
    // Port-draft mass ceiling this load could reach (the total-on-board draft budget expressed as
    // this load's own mass). Finite only when a transited port draft constrained the load; equals
    // `massLoaded` exactly when draft was the binding term, strictly above it otherwise.
    let loadDraftMassCap = Infinity;

    const deckCargo =
      isLoadEvent(event) || isDischargeEvent(event)
        ? isDeckCargo(event.order.orderDetailsByOrderId.nodes)
        : false;

    if (isLoadEvent(event)) {
      const { order } = event;

      state.ordersOnBoard++;

      if (order.assortmentId != null) {
        state.carriedAssortmentsByOrderId.set(order.id, {
          assortmentId: order.assortmentId,
          assortmentGroupId:
            order.assortmentByAssortmentId?.assortmentGroupByAssortmentGroup
              ?.id ?? null,
        });
      }

      start = Math.max(sofCommenced ?? 0, startPortActivity);
      // SEABER-6722: operations start is per order — set on every L/D event so a
      // continuation load measures from its own start, not the shared discharge.
      state.currentPortCallOperationsStart = start;
      const cargoRange = order.cargoRanges;
      const maxMass = cargoRange.mass.max;
      const maxVolume = cargoRange.volume.max;

      const hasMatchingCompartment = !!setFind(
        state.loadedCompartmentsById,
        (c) =>
          isCorrectCargoAllocationType(
            order.assortmentByAssortmentId?.cargoType ?? null,
            c.compartment,
          ),
      );

      const totalVolumeByAllocations =
        state.loadedCompartmentsById.size == 0
          ? // Client doesn't use compartments on this vessel, assume all fits
            maxVolume
          : !hasMatchingCompartment
            ? // Compartments defined but wrong type, nothing fits
              0
            : !event.isNew &&
                !event.cargoAllocations?.some((a) => +a.amount > 0)
              ? // Allocations not defined (yet?), assume all fits (helps legacy data, also allows overriding tank logic when necessary)
                Infinity
              : getAndAddLoadedAmountToCompartments(
                  order.assortmentByAssortmentId,
                  event.cargoAllocations ?? [],
                  state.loadedCompartmentsById,
                );

      state.orderToLoadIndex.set(order.id, i);
      const maxAllowedAmount = getMaxAllowedLoadAmount(
        event,
        {
          mass: state.massCapacityAvailable,
          volume: deckCargo
            ? state.deckVolumeCapacityAvailable
            : state.volumeCapacityAvailable,
        },
        overlappingLoadsByOrder.get(order.id) ?? [],
      );
      // Port-draft mass ceiling for this load, enforced per span it transits against the shared span
      // budget. Physical term = the hard headroom left in the tightest span; reservation term reserves
      // the band minimum of every later co-onboard order in each span (mirroring the mass/volume
      // reservation) but floors at this order's own minimum so a genuinely infeasible set spills the
      // later load forward rather than starving this one. Both stay Infinity when no span constrains
      // this load, or when the cargo carries no mass dimension for the draft to act on.
      const draftSegmentIndices = draftCeiling?.segmentIndicesByOrderId.get(
        order.id,
      );
      let draftPhysicalMax = Infinity;
      let draftReservedMax = Infinity;
      if (draftCeiling && draftSegmentIndices && maxMass > 0) {
        let physical = Infinity;
        let reserved = Infinity;
        for (const s of draftSegmentIndices) {
          const cap = draftCeiling.segments[s].draftMassCap;
          if (!Number.isFinite(cap)) continue;
          const decided = draftCeiling.decidedMass[s];
          const pendingMinMass =
            draftCeiling.segments[s].totalMinMass -
            draftCeiling.decidedMinMass[s] -
            cargoRange.mass.min;
          physical = Math.min(physical, (cap - decided) / maxMass);
          reserved = Math.min(
            reserved,
            (cap - decided - pendingMinMass) / maxMass,
          );
        }
        draftPhysicalMax = physical;
        draftReservedMax = Math.max(cargoRange.mass.min / maxMass, reserved);
        const draftRatio = Math.min(draftPhysicalMax, draftReservedMax);
        if (Number.isFinite(draftRatio))
          loadDraftMassCap = draftRatio * maxMass;
      }
      // A dimension with a zero band max is absent (mass-unit cargo without stowage factor or
      // specific gravity carries volume {0,0}, and vice versa) and must not constrain the load —
      // but the other dimension still does. Only when BOTH are absent is the load unconstrained.
      const capacityFeasibleMax =
        maxMass === 0 && maxVolume === 0
          ? 1
          : Math.min(
              maxAllowedAmount,
              maxMass > 0 ? state.massCapacityAvailable / maxMass : Infinity,
              maxVolume > 0
                ? Math.min(
                    deckCargo
                      ? state.deckVolumeCapacityAvailable / maxVolume
                      : state.volumeCapacityAvailable / maxVolume,
                    (totalVolumeByAllocations ?? Infinity) / maxVolume,
                  )
                : Infinity,
              draftPhysicalMax,
              draftReservedMax,
              1,
            );
      const explicitRatio = explicitLoadRatios?.get(order.id);
      if (explicitRatio === undefined) {
        // A per-piece (pcs/set) order can only be loaded in whole pieces. Snap the capacity-feasible
        // load down to a whole piece within the band, income-blind (largest whole piece the capacity
        // allows, ≥ the band minimum); when the band spans no whole piece, load the entered value.
        // Single-cargo committed voyages take this branch — they get no fill overlay — so the snap must
        // live here for them to match the multi-cargo overlay's whole-piece loads. For pcs/set the mass
        // dimension is a piece count, so maxMass·ratio is the loaded piece count and cargoAmount is the
        // entered piece count (identity system-unit conversion).
        if (
          isPerPieceCargoUnit(order.cargoUnitByCargoUnitId?.name) &&
          maxMass > 0
        ) {
          const loadedPieces = chooseLoadedPieces(
            cargoRange.mass.min,
            maxMass,
            capacityFeasibleMax * maxMass,
            Number(order.cargoAmount),
          );
          amount = loadedPieces / maxMass;
        } else {
          amount = capacityFeasibleMax;
        }
      } else {
        // floorRatio is the ratio at which this load equals its band minimum on the binding
        // dimension — the same baseline the income-prioritized fill seeds from. A raise (ratio at or
        // above the floor) is clamped to [floorRatio, capacityFeasibleMax], flooring the upper bound to
        // floorRatio so clamp never runs lower>upper if a co-loaded order consumed shared headroom
        // earlier in this pass. A below-floor ratio is an income-ordered reduction: honored as given
        // but still capped above by capacityFeasibleMax, so the derive can only cut it further, never
        // inflate it back up to the floor and override the reduce choice.
        const floorRatio =
          maxMass === 0 && maxVolume === 0
            ? 1
            : Math.max(
                maxMass > 0 ? cargoRange.mass.min / maxMass : 0,
                maxVolume > 0 ? cargoRange.volume.min / maxVolume : 0,
              );
        amount =
          explicitRatio >= floorRatio
            ? clamp(
                explicitRatio,
                floorRatio,
                Math.max(floorRatio, capacityFeasibleMax),
              )
            : Math.min(explicitRatio, capacityFeasibleMax);
      }
      massLoaded = maxMass * amount;
      volumeLoaded = maxVolume * amount;
      // Commit this load's mass and band minimum to every span it transits so the next co-onboard load
      // budgets against the updated span totals.
      if (draftCeiling && draftSegmentIndices) {
        for (const s of draftSegmentIndices) {
          draftCeiling.decidedMass[s] += massLoaded;
          draftCeiling.decidedMinMass[s] += cargoRange.mass.min;
        }
      }
      holdVolume = deckCargo ? 0 : volumeLoaded;
      deckVolume = deckCargo ? volumeLoaded : 0;
      state.totalMass += massLoaded;
      state.massCapacityAvailable -= massLoaded;
      state.volumeCapacityAvailable -= holdVolume;
      state.deckVolumeCapacityAvailable -= deckVolume;
      state.massAboard += massLoaded;
      state.holdVolumeAboard += holdVolume;
      state.deckVolumeAboard += deckVolume;

      // Track on-board MINIMUM cargo separately so suggestion admission can test fit-at-minimum,
      // independent of how much the forward pass actually filled toward max.
      const minMass = cargoRange.mass.min;
      const minVolume = cargoRange.volume.min;
      state.minMassUsed += minMass;
      if (deckCargo) {
        state.minDeckVolumeUsed += minVolume;
      } else {
        state.minHoldVolumeUsed += minVolume;
      }

      pcs += event.pcs;

      const {
        rate: loadingRate,
        unit: loadingUnit,
        source: loadingRateSource,
      } = getEffectiveCargoHandlingRate(
        order,
        event.entry,
        vessel,
        coasById,
        config,
        'load',
      );

      work = calculateWorkShifts(
        order.locationByLoadPortId,
        start,
        getLoadingTime(
          massLoaded,
          volumeLoaded,
          loadingRate,
          loadingUnit,
          msToH(Number(order.loadingOverheadDuration ?? 0)),
        ),
        getOrderLaytimeDays('loading', order, coasById),
      );
      calculatedDuration = hToMs(
        ceilIfEnabled(work.duration, config.useRoundedEstimations),
      );

      inputs.loadingRate = {
        value: loadingRate,
        unit: loadingUnit,
        source: loadingRateSource,
      };

      duration =
        durationOverride != null
          ? durationOverride
          : nextEventStartOverride
            ? Math.min(nextEventStartOverride - start - 1, calculatedDuration)
            : calculatedDuration;

      const { cancelling } = constraints;

      // SEABER-6722: asymmetric under ETA — early from the prior discharge end
      // (violationArrival), late from the actual arrival.
      if (layday && violationArrival < layday) {
        cancellingOrDeliveryViolationVsEta = msToH(violationArrival - layday);
      } else if (cancelling && state.arriveInPort > cancelling) {
        cancellingOrDeliveryViolationVsEta = msToH(
          state.arriveInPort - cancelling,
        );
      } else {
        cancellingOrDeliveryViolationVsEta = 0;
      }
      if (layday && start < layday) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - layday);
      } else if (cancelling && start > cancelling) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - cancelling);
      } else {
        cancellingOrDeliveryViolationVsEvent = 0;
      }
      if (layday && state.currentPortCallOperationsStart < layday) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - layday,
        );
      } else if (
        cancelling &&
        state.currentPortCallOperationsStart > cancelling
      ) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - cancelling,
        );
      } else {
        cancellingOrDeliveryViolationVsOperations = 0;
      }
    } else if (isDischargeEvent(event)) {
      const { order } = event;

      state.ordersOnBoard--;

      if (order.assortmentId != null) {
        state.carriedAssortmentsByOrderId.delete(order.id);
      }

      start = Math.max(sofCommenced ?? 0, startPortActivity);
      state.currentPortCallOperationsStart = start;
      const loadIndex = state.orderToLoadIndex.get(order.id);
      const indexDistance = i - (loadIndex ?? 0);
      if (loadIndex === undefined || indexDistance < 1) {
        throw new Error(
          `Invalid port rotation: Order ${order.id} discharged before loading`,
        );
      }
      hasMiddle =
        indexDistance > 1 &&
        findIndexWith(events, loadIndex + 1, 1, (e) => !e.skipOnSwap, i - 1) !==
          undefined;
      const loadEvent = arr[loadIndex];
      loadEvent.hasMiddle = hasMiddle;
      amount = loadEvent.amount;
      massLoaded = loadEvent.mass;
      volumeLoaded = loadEvent.volume;
      holdVolume = deckCargo ? 0 : volumeLoaded;
      deckVolume = deckCargo ? volumeLoaded : 0;
      state.massCapacityAvailable += massLoaded;
      state.volumeCapacityAvailable += holdVolume;
      state.deckVolumeCapacityAvailable += deckVolume;
      state.massAboard -= massLoaded;
      state.holdVolumeAboard -= holdVolume;
      state.deckVolumeAboard -= deckVolume;

      // Mirror the load branch's MINIMUM-quantity accounting: release this order's band minimum so
      // the available-at-minimum snapshot reflects only orders still on board.
      const minMass = order.cargoRanges.mass.min;
      const minVolume = order.cargoRanges.volume.min;
      state.minMassUsed -= minMass;
      if (deckCargo) {
        state.minDeckVolumeUsed -= minVolume;
      } else {
        state.minHoldVolumeUsed -= minVolume;
      }

      pcs += event.pcs;
      releaseOrderFromCompartments(order.id, state.loadedCompartmentsById);

      const {
        rate: dischargeRate,
        unit: dischargeUnit,
        source: dischargeRateSource,
      } = getEffectiveCargoHandlingRate(
        order,
        event.entry,
        vessel,
        coasById,
        config,
        'discharge',
      );

      work = calculateWorkShifts(
        order.locationByDischargePortId,
        start,
        getLoadingTime(
          massLoaded,
          volumeLoaded,
          dischargeRate,
          dischargeUnit,
          msToH(Number(order.dischargingOverheadDuration ?? 0)),
        ),
        getOrderLaytimeDays('discharge', order, coasById),
      );
      calculatedDuration = hToMs(
        ceilIfEnabled(work.duration, config.useRoundedEstimations),
      );

      inputs.dischargeRate = {
        value: dischargeRate,
        unit: dischargeUnit,
        source: dischargeRateSource,
      };

      duration =
        durationOverride != null
          ? durationOverride
          : nextEventStartOverride
            ? Math.min(nextEventStartOverride - start - 1, calculatedDuration)
            : calculatedDuration;

      const { deliveredBefore } = constraints;
      if (rta && state.arriveInPort < rta) {
        cancellingOrDeliveryViolationVsEta = msToH(state.arriveInPort - rta);
      } else if (deliveredBefore && state.arriveInPort > deliveredBefore) {
        cancellingOrDeliveryViolationVsEta = msToH(
          state.arriveInPort - deliveredBefore,
        );
      } else {
        cancellingOrDeliveryViolationVsEta = 0;
      }
      if (rta && start < rta) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - rta);
      } else if (deliveredBefore && start > deliveredBefore) {
        cancellingOrDeliveryViolationVsEvent = msToH(start - deliveredBefore);
      } else {
        cancellingOrDeliveryViolationVsEvent = 0;
      }
      if (rta && state.currentPortCallOperationsStart < rta) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - rta,
        );
      } else if (
        deliveredBefore &&
        state.currentPortCallOperationsStart > deliveredBefore
      ) {
        cancellingOrDeliveryViolationVsOperations = msToH(
          state.currentPortCallOperationsStart - deliveredBefore,
        );
      } else {
        cancellingOrDeliveryViolationVsOperations = 0;
      }
    } else if (type === CoreEventType.Single) {
      start = startPortActivity;
      calculatedDuration = isBoundaryPairContinuation
        ? 0
        : Math.max(event.duration, minimumEventDuration);
      duration = isBoundaryPairContinuation ? 0 : event.duration;
      cancellingOrDeliveryViolationVsEta = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it
      cancellingOrDeliveryViolationVsOperations = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it
      cancellingOrDeliveryViolationVsEvent = 0; // Single events (& Port calls) don't have laycan/delivery date and hence cannot violate it

      const exclusion = voyageExclusionsById.get(
        event.cleaningAssortmentExclusionId,
      );
      if (exclusion) {
        cleaningCost = Number(exclusion.holdCleaningCost) ?? 0;

        // Check if the duration is outdated and needs updating based on exclusion
        const expectedDuration = hToMs(Number(exclusion.holdCleaningTimeHours));
        if (expectedDuration !== duration) {
          calculatedDuration = Math.max(expectedDuration, minimumEventDuration);
          duration = calculatedDuration;
        }
      }

      const arrival = get(events, i - 2);
      const { portCallId } = event;
      if (
        arrival &&
        portCallId &&
        isPortCallArrivalEvent(arrival) &&
        portCallId === arrival.portCallId
      ) {
        const arrivalTd = arr[i - 2];
        hoursAtSea = arrivalTd.hoursAtSea;
      }
    } else if (type === CoreEventType.OffHire) {
      start = event.start;
      calculatedDuration = event.end - event.start;
      duration = calculatedDuration;
      const arrivalAndStartDelta = arriveToEvent - start;

      cancellingOrDeliveryViolationVsEta = msToH(
        Math.max(arrivalAndStartDelta, 0),
      );
      cancellingOrDeliveryViolationVsOperations = msToH(
        Math.max(startPortActivity - start, 0),
      );
      cancellingOrDeliveryViolationVsEvent = cancellingOrDeliveryViolationVsEta;
    } else if (type === CoreEventType.Static) {
      if (!isTcInEvent(event)) {
        exhaustiveCheck(
          event,
          'DeriveCapacitiesAndTimes only supports static events of type TC In',
        );
      }

      start = Math.max(arriveToEvent, event.start);
      // When determining the duration we ensure that the event is visible even
      // when it's pushed past it's defined end date by the previous event
      calculatedDuration = Math.max(event.end - start, dToMs(1));
      duration = calculatedDuration;

      const redeliveryEndDate = event.entry.redelivery?.endDate;
      const redeliveryViolation = redeliveryEndDate
        ? Math.max(msToH(arriveToEvent - redeliveryEndDate), 0)
        : 0;

      cancellingOrDeliveryViolationVsEta = redeliveryViolation;
      cancellingOrDeliveryViolationVsOperations = redeliveryViolation;
      cancellingOrDeliveryViolationVsEvent = redeliveryViolation;
    } else {
      throw new Error(`Unexpected event type, ${category} / ${type}`);
    }

    const hoursWaitingOrRtaBuffer = msToH(start - arriveToEvent);

    if (isConcurrentWithPrevious && prevTd) {
      start = Math.max(start, prevTd.start + 1);
    }

    const minDuration = isBoundaryPairContinuation ? 0 : minimumEventDuration;
    duration = Math.max(minDuration, duration);

    // If there is a factual end given take it into consideration and recalculate duration.
    const sofEnd =
      sofCommenced && sofDuration ? sofCommenced + sofDuration : null;
    const end = Math.max(
      start + minDuration,
      Math.min(start + duration, sofEnd ?? Infinity),
    );
    duration = end - start;

    const signedDelay = cancellingOrDeliveryViolationVsEta;
    const delay = Math.abs(signedDelay);
    const constrainedDelay = Math.min(DELAY_CONSTRAINT_HOURS, delay); // Consider at most 30 days delay (30d * 24h = 720h)

    state.delays += delay;
    state.constrainedDelays += constrainedDelay;

    const hoursAtPort = msToH(end - arriveToEvent);
    const usedSpeed = distance / hoursAtSea;

    let insideEcaData: EnrichedSpeedConsumption | undefined = undefined;
    let insideEcaDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let outsideEcaData: EnrichedSpeedConsumption | undefined = undefined;
    let outsideEcaDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let bunkerPortData: EnrichedSpeedConsumption | undefined = undefined;
    let bunkerPortDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;
    let extraConsumptionData: EnrichedSpeedConsumption | undefined = undefined;
    let extraConsumptionDataSecondary: EnrichedSpeedConsumption | undefined =
      undefined;

    const insideEcaDistance = routeData?.inside_nm ?? 0;
    const outsideEcaDistance = routeData?.outside_nm ?? 0;

    const insideEcaHours = routeData?.inside_nm
      ? routeData.inside_nm / usedSpeed
      : 0;

    const outsideEcaHours = routeData?.outside_nm
      ? routeData.outside_nm / usedSpeed
      : 0;

    const area = determineVesselArea(routeData);
    const portCallEntry = currentPortCallArrival?.entry ?? null;
    // Attached to the port call entry
    const bunkerOverrides = portCallEntry?.bunkerOverrides ?? null;
    const weatherFactor = isPortCallArrivalEvent(event)
      ? event.weatherFactor
      : undefined;

    const commonProps = {
      bunkerPriceByGradeAndPortCode,
      vessel,
      currencyRates: config.currencyRates,
      portBunkerOverrides: bunkerOverrides,
      orgDefaultBunkerGradeIdsByArea,
      weatherFactor,
      bunkerGradeCalculations,
      resolvedBunkerGrades,
    };
    const nonPortCommonProps = {
      ...commonProps,
      targetSpeed: usedSpeed,
      type: laden ? VesselSpeedType.LADEN : VesselSpeedType.BALLAST,
    };
    const portCommonProps = {
      ...commonProps,
      distance: 0,
      targetSpeed: 0,
      type: VesselSpeedType.PORT,
    };

    if (calculateCosts && insideEcaDistance > 0) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.INSIDE_ECA,
        calculatedHours: insideEcaHours,
        distance: insideEcaDistance,
        ...nonPortCommonProps,
      });
      if (result) {
        insideEcaData = result.primary;
        insideEcaDataSecondary = result.secondary;
      }
    }
    if (calculateCosts && outsideEcaDistance > 0) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.OUTSIDE_ECA,
        distance: outsideEcaDistance,
        calculatedHours: outsideEcaHours,
        ...nonPortCommonProps,
      });
      if (result) {
        outsideEcaData = result.primary;
        outsideEcaDataSecondary = result.secondary;
      }
    }

    // For distance overrides inside/outside eca distance is not currently available
    if (
      calculateCosts &&
      !insideEcaData &&
      !outsideEcaData &&
      routeData?.is_override
    ) {
      const result = getNearestSpeedConsumption({
        vesselArea: VesselArea.ALL_AREAS,
        calculatedHours: hoursAtSea,
        distance: distance,
        ...nonPortCommonProps,
      });
      if (result) {
        outsideEcaData = result.primary;
        outsideEcaDataSecondary = result.secondary;
      }
    }

    if (calculateCosts && hoursAtPort > 0) {
      const portArea = determinePortArea(
        isPortCallEvent(event) ? event.location : null,
      );
      const result = getNearestSpeedConsumption({
        vesselArea: portArea,
        calculatedHours: hoursAtPort,
        isLoading: isLoadEvent(event),
        isCargoEvent: isCargoEvent(event),
        ...portCommonProps,
      });
      if (result) {
        bunkerPortData = result.primary;
        bunkerPortDataSecondary = result.secondary;
      }
    }

    const extraPortConsumptionHours =
      (insideEcaData?.idleHours ?? 0) +
      (outsideEcaData?.idleHours ?? 0) +
      extraPortHours;

    if (calculateCosts && extraPortConsumptionHours > 0) {
      const portArea = determinePortArea(
        isPortCallEvent(event) ? event.location : null,
      );
      const result = getNearestSpeedConsumption({
        vesselArea: portArea,
        calculatedHours: extraPortConsumptionHours,
        isLoading: false,
        isCargoEvent: false,
        isExtraPortConsumptionHours: true,
        ...portCommonProps,
      });
      if (result) {
        extraConsumptionData = result.primary;
        extraConsumptionDataSecondary = result.secondary;
      }
    }

    const portCostHours = isConcurrentWithPrevious
      ? Math.max(0, hoursAtPort - state.concurrentPortHours)
      : hoursAtPort;
    state.concurrentPortHours = isConcurrentWithPrevious
      ? Math.max(hoursAtPort, state.concurrentPortHours)
      : hoursAtPort;
    const bunkerCostInside = Number(insideEcaData?.voyageCost ?? 0);
    const bunkerCostInsideSecondary = Number(
      insideEcaDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostOutside = Number(outsideEcaData?.voyageCost ?? 0);
    const bunkerCostOutsideSecondary = Number(
      outsideEcaDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostPort = Number(bunkerPortData?.voyageCost ?? 0);
    const bunkerCostPortSecondary = Number(
      bunkerPortDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostExtra = Number(extraConsumptionData?.voyageCost ?? 0);
    const bunkerCostExtraSecondary = Number(
      extraConsumptionDataSecondary?.voyageCost ?? 0,
    );
    const bunkerCostTotal =
      bunkerCostInside +
      bunkerCostInsideSecondary +
      bunkerCostOutside +
      bunkerCostOutsideSecondary +
      bunkerCostPort +
      bunkerCostPortSecondary +
      bunkerCostExtra +
      bunkerCostExtraSecondary;
    state.bunkerCost += bunkerCostTotal;

    const seaCost = calculateCosts ? hoursAtSea * tcRate : 0;
    const portCost = calculateCosts ? portCostHours * tcRate : 0;

    const tcCost = seaCost + portCost;
    const voyageCost = bunkerCostTotal + portCostEstimate + channelCost;

    const total = voyageCost + tcCost;

    state.cost += total;
    state.tcCostTotal += tcCost;
    state.totalHoursAtSea += hoursAtSea;
    state.totalHoursAtPort += portCostHours;

    const totalConsumedBunkers =
      (insideEcaData?.consumedBunker ?? 0) +
      (insideEcaDataSecondary?.consumedBunker ?? 0) +
      (outsideEcaData?.consumedBunker ?? 0) +
      (outsideEcaDataSecondary?.consumedBunker ?? 0) +
      (bunkerPortData?.consumedBunker ?? 0) +
      (bunkerPortDataSecondary?.consumedBunker ?? 0) +
      (extraConsumptionData?.consumedBunker ?? 0) +
      (extraConsumptionDataSecondary?.consumedBunker ?? 0);

    let totalCO2 = 0;
    if (insideEcaData) {
      const carbonFactor = insideEcaData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * insideEcaData.consumedBunker;
    }
    if (insideEcaDataSecondary) {
      const carbonFactor =
        insideEcaDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * insideEcaDataSecondary.consumedBunker;
    }

    if (outsideEcaData) {
      const carbonFactor = outsideEcaData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * outsideEcaData.consumedBunker;
    }
    if (outsideEcaDataSecondary) {
      const carbonFactor =
        outsideEcaDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * outsideEcaDataSecondary.consumedBunker;
    }

    if (bunkerPortData) {
      const carbonFactor = bunkerPortData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * bunkerPortData.consumedBunker;
    }
    if (bunkerPortDataSecondary) {
      const carbonFactor =
        bunkerPortDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * bunkerPortDataSecondary.consumedBunker;
    }

    if (extraConsumptionData) {
      const carbonFactor =
        extraConsumptionData.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * extraConsumptionData.consumedBunker;
    }
    if (extraConsumptionDataSecondary) {
      const carbonFactor =
        extraConsumptionDataSecondary.carbonFactor ?? DEFAULT_CARBON_FACTOR;
      totalCO2 += carbonFactor * extraConsumptionDataSecondary.consumedBunker;
    }

    const key = getEventKey(event);

    // Assortment exclusions for the current shipment
    const shipmentId = getEventOrder(event)?.shipmentId;
    const assortmentExclusions =
      shipmentId !== undefined
        ? (assortmentExclusionsByShipmentId.get(shipmentId) ?? [])
        : [];

    const td = {
      area,
      assortmentExclusions,
      key,
      hoursDelayConstraint,
      isFirstEvent,
      isLastEvent,
      isFirstShipmentEvent,
      hasMiddle,
      canPortCallSwapLeft: false,
      canPortCallSwapRight: false,
      index: i,
      arriveInPort: state.arriveInPort,
      violationArrival,
      start,
      operationsStart: state.currentPortCallOperationsStart,
      calculatedDuration,
      work,
      end,
      laden,
      ordersOnBoard: state.ordersOnBoard,
      amount,
      draftMassCap: loadDraftMassCap,
      mass: isDischargeEvent(event) ? -massLoaded : massLoaded,
      volume: isDischargeEvent(event) ? -volumeLoaded : volumeLoaded,
      holdVolume: isDischargeEvent(event) ? -holdVolume : holdVolume,
      deckVolume: isDischargeEvent(event) ? -deckVolume : deckVolume,
      pcs: isDischargeEvent(event) ? -pcs : pcs,
      previousEventStart: prevTd?.start ?? null,
      hoursAtSea,
      channelDurationOverhead,
      calculatedSeaDuration,
      hoursAtPort,
      totalHours: hoursAtSea + hoursAtPort,
      portActivities: msToH(duration),
      hoursWaitingOrRtaBuffer,
      cancellingOrDeliveryViolationVsEta,
      cancellingOrDeliveryViolationVsOperations,
      cancellingOrDeliveryViolationVsEvent,
      physicalTimeConflict: Math.max(0, arriveToEvent - start),
      startOverrideConflict: Math.max(
        0,
        start - (startOverride ?? start) - minimumEventDuration,
      ),
      costs: {
        bunkerGrade: {
          insideEcaData,
          insideEcaDataSecondary,
          outsideEcaData,
          outsideEcaDataSecondary,
          portData: bunkerPortData,
          portDataSecondary: bunkerPortDataSecondary,
          extraConsumptionData,
          extraConsumptionDataSecondary,
          total: bunkerCostTotal,
        },
        tcCostSea: seaCost,
        tcCostPort: portCost,
        tcCost,
        totalCost: total,
        portCostEstimate,
        channelCost,
        cleaningCost,
      },
      totalCO2,
      totalConsumedBunkers,
      usedChannels: routeData?.used_channels ?? [],
      bannedChannelsInRoute,
      distance,
      distanceUnresolved,
      event,
      availableVolumeCapacity: state.volumeCapacityAvailable,
      availableDeckVolumeCapacity: state.deckVolumeCapacityAvailable,
      availableMassCapacity: state.massCapacityAvailable,
      availableMinMassCapacity: massCap - state.minMassUsed,
      availableMinVolumeCapacity: volumeCap - state.minHoldVolumeUsed,
      availableMinDeckVolumeCapacity: deckVolumeCap - state.minDeckVolumeUsed,
      minMassAboard: state.minMassUsed,
      usedMassCapacity: state.massAboard,
      usedVolumeCapacity: state.holdVolumeAboard,
      usedDeckVolumeCapacity: state.deckVolumeAboard,
      duration,
      delays: delay,
      signedDelays: signedDelay,
      constrainedDelays: constrainedDelay,
      inputs,
    };

    state.prev = endLocationId;
    state.previousEnd = Math.max(state.previousEnd, end);
    if (isDischargeEvent(event)) {
      // Concurrent discharges are processed in array order, not end order (see the
      // Math.max on previousEnd above), so keep the latest end — a following
      // continuation load measures its violation arrival from the true last discharge.
      state.currentPortCallLastDischargeEnd = Math.max(
        state.currentPortCallLastDischargeEnd ?? end,
        end,
      );
    }
    if (isFirstShipmentEvent) {
      state.hasFirstShipmentEvent = true;
    }

    arr[i] = td;
    if (writeTimeDataCache) {
      cacheTimeData(
        event,
        prevTd ?? null,
        [td, cloneDeriveCapacitiesAndTimesState(state)],
        loadAmountCacheKeyByEvent.get(event) ?? null,
      );
    }
  }

  if (!skipSwapComputation) {
    // Detect if each location/port call can be swapped to either direction
    applyPortCallSwapInfoToTimeData(arr);
  }

  return {
    timeData: arr,
    totals: {
      totalHours: state.totalHoursAtSea + state.totalHoursAtPort,
      hoursAtSea: state.totalHoursAtSea,
      hoursAtPort: state.totalHoursAtPort,
      ballastDistance: state.ballastDistance,
      distance: state.totalDistance,
      unresolvedLegs: state.unresolvedLegs,
      mass: state.totalMass,
      bunkerCost: state.bunkerCost,
      portCost: state.portCostEstimateTotal,
      delays: state.delays,
      constrainedDelays: state.constrainedDelays,
      cost: state.cost,
      tcCost: state.tcCostTotal,
    },
    inputs: {
      vesselId: vessel.id,
      weightCapacity,
      volumeCapacity,
      deckVolumeCapacity,
      tcRate,
    },
  };
};

/**
 * Determine the port type (L/D/M/F) from events in a port call. Returns undefined if no port type is found.
 *
 * Note: Currently we distinguish between loading, discharging, M and bunkering port calls.
 * Other port types are not supported yet.
 * */
export function getPortTypeFromEvents(
  events: Events[],
  portCallId: ClientId | null,
  visitResolver: BoundaryVisitResolver,
): PortType | undefined {
  if (!portCallId) {
    return undefined;
  }

  // A boundary pair is one physical visit: classify across all its port
  // calls so cost-source matching sees the merged call's port type.
  const visitPortCallIds = visitResolver.getVisitPortCallIds(portCallId);
  const portCallEvents = events.filter(
    (e) => e.portCallId !== null && visitPortCallIds.has(e.portCallId),
  );

  const hasLoadEvent = portCallEvents.some(isLoadEvent);
  const hasDischargeEvent = portCallEvents.some(isDischargeEvent);

  if (hasLoadEvent && hasDischargeEvent) {
    return PortType.M;
  }
  if (hasLoadEvent) {
    return PortType.L;
  }
  if (hasDischargeEvent) {
    return PortType.D;
  }

  // Check if this is a bunkering port call (has a bunkering single event)
  const hasBunkeringEvent = portCallEvents.some(
    (e) => isSingleEvent(e) && e.event === SingleEventType.BUNKERING,
  );
  if (hasBunkeringEvent) {
    return PortType.F;
  }
  return undefined;
}

const getSortedPriorityKeys = memoize(function getSortedPriorityKeys(
  portCostPriority: PortCostPriority,
) {
  return portCostPriorityKeys.toSorted(
    (a, b) =>
      (portCostPriority[a] ?? Infinity) - (portCostPriority[b] ?? Infinity),
  );
});

/** Get the effective port cost override/default/estimate based on priority */
export function getEffectivePortCost(
  event: Events,
  portCostEstimateMaps: PortCostEstimateMaps,
  portCostPriority: PortCostPriority = DEFAULT_PORT_COST_PRIORITY,
  vessel: Vessel & { name: string },
  locationsById: CalculationConfig['locationsById'],
  portType?: PortType,
) {
  const {
    byName,
    byPrimaryAttributeId,
    latestByPrimaryAttributeId,
    defaultById,
    defaultByTypeId,
    defaultByVesselId,
    locationIdsByName,
  } = portCostEstimateMaps;

  const location = getEventLocation(event, locationsById);
  const locationName = location?.name;
  const locationId = location?.id;
  const primaryVesselAttributeId = vessel.primaryAttributeId;
  let oneTimeOverrideCost = undefined;
  let portCostDefaultId = undefined;
  let useHistoricalPortCost = undefined;
  let useLatestHistoricalPortCost = undefined;
  let useOneTimeOverrideCost = undefined;

  if (isPortCallArrivalEvent(event)) {
    oneTimeOverrideCost = event.oneTimeOverrideCost;
    portCostDefaultId = event.portCostDefaultId;
    useHistoricalPortCost = event.useHistoricalPortCost;
    useLatestHistoricalPortCost = event.useLatestHistoricalPortCost;
    useOneTimeOverrideCost = event.useOneTimeOverrideCost;
  }

  // Handle explicitly selected sources first for performance
  if (useOneTimeOverrideCost && oneTimeOverrideCost != null) {
    return oneTimeOverrideCost;
  }
  if (portCostDefaultId) {
    return defaultById.get(portCostDefaultId) ?? 0;
  }

  const estimate = getHistoricalPortCostEstimates({
    locationName,
    vesselName: vessel.name,
    primaryVesselAttributeId,
    byName,
    byPrimaryAttributeId,
    portType,
  });

  if (useHistoricalPortCost) {
    return estimate?.cost ?? 0;
  }

  if (useLatestHistoricalPortCost && locationName) {
    const latestByAttribute = getLatestHistoricalPortCost(
      locationName,
      primaryVesselAttributeId,
      latestByPrimaryAttributeId,
      portType,
    );
    if (latestByAttribute?.cost !== undefined) {
      return latestByAttribute.cost;
    }
  }

  // NOTE: Early return for integration cost would be here if the data was available. (getIntegrationPortCosts)

  // Fallback to priority-based selection if no source was explicitly chosen
  const costValues: Record<keyof PortCostPriority, number | undefined> = {
    portCostDefault: undefined,
    portCostDefaultMax: undefined,
    portCostHistory: estimate?.cost,
    portCostHistoryLatest: undefined,
    portCostOverride: undefined,
    portCostIntegration: undefined,
  };

  if (oneTimeOverrideCost) {
    costValues.portCostOverride = oneTimeOverrideCost;
  }

  let defaultCost: number | undefined;
  if (primaryVesselAttributeId) {
    const defaultByVesselClassCost = getDefaultPortCostByType(
      defaultByTypeId,
      location.id,
      primaryVesselAttributeId,
      locationIdsByName,
    );
    if (defaultByVesselClassCost) {
      defaultCost = defaultByVesselClassCost.cost;
    }
  }

  const defByNameCost = defaultByVesselId.get(locationId, vessel.id);
  if (defaultCost === undefined && defByNameCost) {
    defaultCost = defByNameCost.cost;
  }

  if (defaultCost === undefined) {
    const typeIds = vessel.vesselAttributesAssociationsByShipId.nodes ?? [];
    for (const type of typeIds) {
      const defByTypeIdCost =
        type?.vesselAttributeId &&
        getDefaultPortCostByType(
          defaultByTypeId,
          location.id,
          type.vesselAttributeId,
          locationIdsByName,
        );
      if (defByTypeIdCost) {
        defaultCost = defByTypeIdCost.cost;
        break;
      }
    }
  }

  if (defaultCost) {
    costValues.portCostDefault = defaultCost;
  }

  // Compute max across ALL default sources at this location (all vessels/types, not just this vessel)
  if (portCostPriority.portCostDefaultMax != null) {
    const locationMaxCost = getMaxDefaultPortCostForLocation(
      locationId,
      portCostEstimateMaps,
    );
    if (locationMaxCost !== undefined) {
      costValues.portCostDefaultMax = locationMaxCost.cost;
    }
  }

  // Sort keys by priority and return the first available value
  const sortedPriorityKeys = getSortedPriorityKeys(portCostPriority);

  for (const key of sortedPriorityKeys) {
    const value = costValues[key];
    if (value != null) {
      return value;
    }
  }

  return 0;
}

function getChannelCost(
  event: Events,
  used_channels: string[],
  laden: boolean,
  carriedAssortmentsByOrderId: DeriveCapacitiesAndTimesState['carriedAssortmentsByOrderId'],
  vessel: Vessel,
  portCostEstimateMaps: PortCostEstimateMaps,
  vesselAttributeById: CalculationConfig['vesselAttributeById'],
  now: number,
) {
  let channelDurationOverhead = 0;

  if (!used_channels?.length) {
    return { channelCost: undefined, channelDurationOverhead };
  }

  const arrivalEvent = isPortCallArrivalEvent(event) ? event : null;

  // All vessel attribute names for matching (checked in order)
  const vesselAttributeNames = vessel.vesselAttributesAssociationsByShipId.nodes
    .map((vaa) => {
      const id = vaa?.vesselAttributeId;
      if (!id) {
        return null;
      }
      return vesselAttributeById.get(id)?.name;
    })
    .filter(isDefined);

  // We use the first carried order's assortment group for channel cost matching
  const firstCarriedOrderAssortmentGroupId =
    mapGetFirstEntry(carriedAssortmentsByOrderId)?.[1]?.assortmentGroupId ??
    null;

  const cargoCondition = laden
    ? ChannelCostCargoCondition.LADEN
    : ChannelCostCargoCondition.BALLAST;

  const baseCriteria: Omit<ChannelCostMatchCriteria, 'channelName'> = {
    vesselId: vessel.id,
    vesselTypeNames:
      vesselAttributeNames.length > 0 ? vesselAttributeNames : null,
    assortmentGroupId: firstCarriedOrderAssortmentGroupId,
    cargoCondition,
  };

  const result = getEffectiveChannelCost({
    channelNames: used_channels,
    oneTimeOverrideChannelCost: arrivalEvent?.oneTimeOverrideChannelCost,
    channelCostDefaultId: arrivalEvent?.channelCostDefaultId,
    channelCostIndex: portCostEstimateMaps.channelCostIndex,
    channelCostRuleById: portCostEstimateMaps.channelById,
    baseCriteria,
  });

  // Only add channel duration overhead if sea duration is not overridden and the event is in future
  const canUseDurationOverhead =
    isPortCallArrivalEvent(event) &&
    !event.seaDurationOverride &&
    event.start >= now;

  if (canUseDurationOverhead) {
    channelDurationOverhead = result.durationOverhead;
  }

  return {
    channelCost: result.cost || undefined,
    channelDurationOverhead,
  };
}

/** Inspects each location in the TimeData sequence and sets canPortCallSwapLeft & canPortCallSwapRight values on all tds at once.
 * Mutates the input.
 * Should be called at the end of DeriveCapacitiesAndTimes on the result.
 *
 * Port call events get flag set to true if ALL individual events inside are eligible to be included in port call swap.
 * Events inside get flag set to true depending on if they themselves are eligible.
 */
export const applyPortCallSwapInfoToTimeData = <EventType extends Events>(
  tds: TimeData<EventType>[],
): void => {
  const tdsByLocation = groupBySequence(tds, (td) =>
    getEventLocationId(td.event),
  );

  /** A TC delivery period holds a location of its own without being a rotation position:
   * attachPortCalls leaves it without port call data and swapPortCall cannot exchange places with
   * it. An off-hire withdraws its location from reordering outright. Treating either as no
   * neighbour keeps eligibility and validation in step. */
  const adjacentRotationPosition = (
    locationIndex: number,
  ): TimeData<EventType>[] | undefined => {
    const locationTds = get(tdsByLocation, locationIndex);
    return locationTds === undefined ||
      locationTds.some((td) => isOffHireEventTimeData(td)) ||
      locationTds.every((td) => isStaticEventTimeData(td))
      ? undefined
      : locationTds;
  };

  tdsByLocation.forEach((locationTds, locationIndex) => {
    // The vessel is off contract for the duration of an off-hire, so the location it starts at
    // keeps its place: there is no neighbour it may trade with in either direction.
    const holdsOffHire = locationTds.some((td) => isOffHireEventTimeData(td));
    const prevLocationEvents = holdsOffHire
      ? undefined
      : adjacentRotationPosition(locationIndex - 1);
    const nextLocationEvents = holdsOffHire
      ? undefined
      : adjacentRotationPosition(locationIndex + 1);

    const tdsByShipment = new Map<
      CargoEvent['order']['shipmentId'],
      TimeData<CargoEvent>[]
    >();

    locationTds.forEach((td) => {
      if (isCargoEventTimeData(td)) {
        const sId = getEventShipmentId(td.event);
        const value = tdsByShipment.get(sId);
        if (value == undefined) {
          tdsByShipment.set(sId, [td]);
        } else {
          value.push(td);
        }
      }
    });

    // Seeded from adjacency, not from `true`: a port call with no cargo has no shipment to fold
    // over, and an empty fold would otherwise claim a move the rotation ends cannot offer.
    let allSwapLeftAllowed = prevLocationEvents !== undefined;
    let allSwapRightAllowed = nextLocationEvents !== undefined;
    let isSharedTds = false;

    for (const shipmentTds of tdsByShipment.values()) {
      isSharedTds = shipmentTds.some((td) => td.event.entry.shared);

      const shipmentOrderIds = shipmentTds.map((td) => td.event.order.id);

      const canShipmentSwapLeft =
        !isSharedTds && prevLocationEvents
          ? !prevLocationEvents.some(
              (td) =>
                isCargoEvent(td.event) &&
                (shipmentOrderIds.includes(td.event.order.id) ||
                  td.event.entry.shared),
            )
          : false;

      const canShipmentSwapRight =
        !isSharedTds && nextLocationEvents
          ? !nextLocationEvents.some(
              (td) =>
                isCargoEvent(td.event) &&
                (shipmentOrderIds.includes(td.event.order.id) ||
                  td.event.entry.shared),
            )
          : false;

      allSwapLeftAllowed &&= canShipmentSwapLeft;
      allSwapRightAllowed &&= canShipmentSwapRight;

      for (const td of shipmentTds) {
        td.canPortCallSwapLeft = canShipmentSwapLeft;
        td.canPortCallSwapRight = canShipmentSwapRight;
      }
    }

    if (allSwapLeftAllowed) {
      for (const td of locationTds) {
        if (isPortCallEventTimeData(td)) {
          td.canPortCallSwapLeft = true;
        }
      }
    }
    if (allSwapRightAllowed) {
      for (const td of locationTds) {
        if (isPortCallEventTimeData(td)) {
          td.canPortCallSwapRight = true;
        }
      }
    }
    //   }
  });
};
